/**
 * `InboxQuery` — one serializable value describing what the user is looking at,
 * and the registries that turn it into a BFF request.
 *
 * The point of the registry shape (rather than a switch in the compiler) is the
 * extensibility contract: adding a filter is one clause in `InboxFilter` plus
 * one entry in `COMPILERS`, with no change to `compileInboxQuery`. Deep links
 * then carry the new filter for free, because they serialize the whole query
 * rather than a hand-listed set of fields.
 */
import { ageBounds } from '@chat-emp/shared';
import type {
  Availability,
  EducationLevel,
  Gender,
  InboxFacetDim,
  InboxFilterClause,
  InboxQueryBody,
  Nationality,
} from '@chat-emp/shared';

/** The coarse cut the inbox header offers. Mirrors the read model's `kind`. */
export type ConversationKind = 'job' | 'direct' | 'info';

/** Channel lifecycle phases, as the read model stores them. */
export type LifecyclePhase = 'open' | 'closing' | 'pending_delete';

/**
 * A filter clause in the client's vocabulary. Deliberately NOT the wire shape:
 * this is what the UI holds, so it uses camelCase and keeps only what the UI
 * actually varies.
 */
export type InboxFilter =
  | { kind: 'kind'; kinds: ConversationKind[] }
  | { kind: 'status'; phases: LifecyclePhase[] }
  | { kind: 'scope'; value: 'need-reply' | 'unread' }
  | { kind: 'job'; jobIds: string[] }
  | { kind: 'counterpart'; counterpartIds: string[] }
  /**
   * `field` picks what the server matches: `name` is the counterpart alone,
   * `any` also reads message bodies. It defaults to `name`, so a caller that
   * has not thought about it keeps the narrower, cheaper search.
   */
  | { kind: 'search'; text: string; field?: 'name' | 'any' }
  | { kind: 'applicationStatus'; stages: string[] }
  | { kind: 'jobLocation'; locations: string[] }
  /**
   * The candidate rows. Each is its own kind rather than one `candidate`
   * clause carrying all five, because `withFilter` replaces by kind: folded
   * into one, clearing Skills would take Education with it.
   *
   * `preferredLocation` is where the candidate says they want to work, which
   * is a different question from `jobLocation` — the posting's own city. The
   * dialog offers only the former; the latter is what the location facets set.
   */
  | { kind: 'skills'; skills: string[] }
  | { kind: 'languages'; languages: string[] }
  | { kind: 'education'; levels: EducationLevel[] }
  | { kind: 'preferredLocation'; locations: string[] }
  | { kind: 'nationality'; nationalities: Nationality[] }
  | { kind: 'availability'; availabilities: Availability[] }
  | { kind: 'gender'; genders: Gender[] }
  | { kind: 'age'; min?: number; max?: number }
  /** Either end may be absent, which is what an empty box means. */
  | { kind: 'expectedSalary'; min?: number; max?: number }
  /** Escape hatch: a wire clause with no client-side vocabulary yet. */
  | { kind: 'custom'; clause: InboxFilterClause };

export type InboxFilterKind = InboxFilter['kind'];

/**
 * Not all of these are offered as a grouping in the UI — `stage` and `location`
 * exist because a grouping is also how a facet dimension is named, and the job
 * and location filters read their options from facets. See GROUP_STRATEGIES.
 */
export type GroupBy = 'none' | 'job' | 'person' | 'stage' | 'location';

export interface GroupSpec {
  by: GroupBy;
}

/** The only ordering the read model's keyset indexes serve. */
export interface SortSpec {
  key: 'last_message_at';
  dir: 'desc';
}

export interface InboxQuery {
  filters: InboxFilter[];
  grouping: GroupSpec;
  sort: SortSpec;
  cursor?: string;
  limit?: number;
}

export function emptyQuery(): InboxQuery {
  return { filters: [], grouping: { by: 'none' }, sort: { key: 'last_message_at', dir: 'desc' } };
}

// ── compiler registry ────────────────────────────────────────────────────────
/**
 * One compiler per clause kind. Returning `[]` drops the clause: an empty
 * selection means "don't narrow", which is both what the UI means by an
 * untouched filter and what the upstream requires (it rejects an empty `IN`
 * list rather than treating it as match-everything).
 */
type Compiler<K extends InboxFilterKind> = (
  filter: Extract<InboxFilter, { kind: K }>,
) => InboxFilterClause[];

type CompilerRegistry = { [K in InboxFilterKind]: Compiler<K> };

const COMPILERS: CompilerRegistry = {
  kind: (f) => (f.kinds.length ? [{ kind: 'kind', kinds: f.kinds }] : []),
  status: (f) => (f.phases.length ? [{ kind: 'status', phases: f.phases }] : []),
  scope: (f) => [{ kind: 'scope', value: f.value }],
  job: (f) => (f.jobIds.length ? [{ kind: 'job', job_ids: f.jobIds }] : []),
  counterpart: (f) =>
    f.counterpartIds.length ? [{ kind: 'counterpart', counterpart_ids: f.counterpartIds }] : [],
  search: (f) => {
    const text = f.text.trim();
    return text ? [{ kind: 'search', field: f.field ?? 'name', text }] : [];
  },
  applicationStatus: (f) =>
    f.stages.length ? [{ kind: 'application_status', stages: f.stages }] : [],
  jobLocation: (f) =>
    f.locations.length ? [{ kind: 'job_location', locations: f.locations }] : [],
  skills: (f) => (f.skills.length ? [{ kind: 'skills', skills: f.skills }] : []),
  languages: (f) => (f.languages.length ? [{ kind: 'languages', languages: f.languages }] : []),
  education: (f) => (f.levels.length ? [{ kind: 'education', education: f.levels }] : []),
  preferredLocation: (f) =>
    f.locations.length ? [{ kind: 'preferred_location', preferred_locations: f.locations }] : [],
  nationality: (f) =>
    f.nationalities.length ? [{ kind: 'nationality', nationalities: f.nationalities }] : [],
  availability: (f) =>
    f.availabilities.length ? [{ kind: 'availability', availabilities: f.availabilities }] : [],
  gender: (f) => (f.genders.length ? [{ kind: 'gender', genders: f.genders }] : []),
  age: (f) => {
    const { min, max } = ageBounds(f);
    if (min === undefined && max === undefined) return [];
    return [
      {
        kind: 'age',
        ...(min === undefined ? {} : { age_min: min }),
        ...(max === undefined ? {} : { age_max: max }),
      },
    ];
  },
  // An untouched range is no cut, the same rule every list clause above keeps —
  // and upstream refuses a salary clause with neither bound rather than reading
  // it as everyone.
  expectedSalary: (f) =>
    f.min === undefined && f.max === undefined
      ? []
      : [
          {
            kind: 'expected_salary',
            ...(f.min === undefined ? {} : { salary_min: f.min }),
            ...(f.max === undefined ? {} : { salary_max: f.max }),
          },
        ],
  custom: (f) => [f.clause],
};

function compileFilter(filter: InboxFilter): InboxFilterClause[] {
  // The registry is keyed by the same discriminant the union uses, so this
  // lookup is total — but TS cannot narrow the callback and the value together.
  const compile = COMPILERS[filter.kind] as Compiler<InboxFilterKind>;
  return compile(filter);
}

/** Compile to the body `POST /api/inbox/query` (and `/facets`) expects. */
export function compileInboxQuery(query: InboxQuery): InboxQueryBody {
  const body: InboxQueryBody = { filters: query.filters.flatMap(compileFilter) };
  if (query.cursor) body.cursor = query.cursor;
  if (query.limit) body.limit = query.limit;
  return body;
}

// ── clause helpers ───────────────────────────────────────────────────────────
/** Set a clause, replacing any existing clause of the same kind. */
export function withFilter(query: InboxQuery, filter: InboxFilter): InboxQuery {
  return {
    ...query,
    filters: [...query.filters.filter((f) => f.kind !== filter.kind), filter],
  };
}

export function withoutFilter(query: InboxQuery, kind: InboxFilterKind): InboxQuery {
  return { ...query, filters: query.filters.filter((f) => f.kind !== kind) };
}

export function filterOf<K extends InboxFilterKind>(
  query: InboxQuery,
  kind: K,
): Extract<InboxFilter, { kind: K }> | undefined {
  return query.filters.find((f) => f.kind === kind) as
    Extract<InboxFilter, { kind: K }> | undefined;
}

// ── group strategies ─────────────────────────────────────────────────────────
/**
 * How a grouping is served: which facet dimension supplies the headers and
 * counts, and how to narrow the query to one section's rows. A grouped view is
 * one facets call plus one scoped query per expanded section — `scope` is what
 * makes the second half possible.
 */
export interface GroupStrategy {
  /** Facet dimension for headers + counts; null when not grouping. */
  facet: InboxFacetDim | null;
  /**
   * The clause narrowing to one facet key. `null` means this grouping cannot
   * be scoped server-side — the caller must fall back to filtering the rows it
   * already has, and must not paginate the section (there is no query to page).
   */
  scope: (key: string) => InboxFilter | null;
}

export const GROUP_STRATEGIES: Record<GroupBy, GroupStrategy> = {
  none: { facet: null, scope: () => null },
  job: { facet: 'job', scope: (key) => ({ kind: 'job', jobIds: [key] }) },
  person: {
    facet: 'person',
    scope: (key) => ({ kind: 'counterpart', counterpartIds: [key] }),
  },
  stage: {
    facet: 'application_status',
    scope: (key) => ({ kind: 'applicationStatus', stages: [key] }),
  },
  location: {
    facet: 'job_location',
    scope: (key) => ({ kind: 'jobLocation', locations: [key] }),
  },
};

/** The query for one section of a grouped view. Identity when not grouping. */
/**
 * The job-less remainder of a job-grouped list.
 *
 * Not a facet key, because the read model has no facet for a NULL column: its
 * facet SQL ends `AND <column> IS NOT NULL`, documented as "column not
 * applicable — not a facet". So a direct chat produces no job group, and in a
 * list that holds both kinds it would have no header to sit under and simply
 * never render. The client owns section assembly, so the client owns the
 * remainder — a reserved key that scopes by kind instead of by job.
 */
export const DIRECT_GROUP_KEY = '__direct__';

/**
 * One of the two cuts the scope pills count. Neither key mentions which pill is
 * pressed, so a press re-fetches nothing: `withFilter` normalises the clause to
 * the tail, `withoutFilter` leaves the rest in order.
 */
export function scopeQuery(query: InboxQuery, cut: 'all' | 'unread'): InboxQuery {
  return cut === 'all'
    ? withoutFilter(query, 'scope')
    : withFilter(query, { kind: 'scope', value: 'unread' });
}

export function scopedQuery(query: InboxQuery, key: string): InboxQuery {
  // Replaces the list's own kind clause rather than adding to it (see
  // `withFilter`), so the section is exactly the direct chats it names.
  if (key === DIRECT_GROUP_KEY) return withFilter(query, { kind: 'kind', kinds: ['direct'] });
  const clause = GROUP_STRATEGIES[query.grouping.by].scope(key);
  return clause ? withFilter(query, clause) : query;
}

// ── serialization ────────────────────────────────────────────────────────────
/**
 * Stored queries are versioned so a shape change is a migration rather than a
 * silent misread. Bump `QUERY_VERSION` whenever `InboxQuery` changes in a way
 * old records cannot satisfy, and handle the old version in `parseQuery`.
 */
export const QUERY_VERSION = 2;

const GROUP_BYS: GroupBy[] = ['none', 'job', 'person', 'stage'];

function isGroupBy(value: unknown): value is GroupBy {
  return typeof value === 'string' && (GROUP_BYS as string[]).includes(value);
}

/**
 * Parse a stored query, returning null when the record cannot be trusted.
 * Filters are taken as-is: they are a closed union written by this module, and
 * an unknown clause kind would simply compile to nothing rather than misbehave.
 */
export function parseQuery(raw: unknown): InboxQuery | null {
  const r = raw as Partial<InboxQuery> | null | undefined;
  if (!r || !Array.isArray(r.filters)) return null;
  const by = r.grouping?.by;
  return {
    filters: r.filters.filter((f): f is InboxFilter => typeof f?.kind === 'string'),
    grouping: { by: isGroupBy(by) ? by : 'none' },
    sort: { key: 'last_message_at', dir: 'desc' },
  };
}

/** The job ids a query narrows to — empty meaning "every job". */
export function jobIdsOf(query: InboxQuery): string[] {
  return filterOf(query, 'job')?.jobIds ?? [];
}

/** The job locations a query narrows to — empty meaning "everywhere". */
export function locationsOf(query: InboxQuery): string[] {
  return filterOf(query, 'jobLocation')?.locations ?? [];
}

// ── local re-evaluation ──────────────────────────────────────────────────────
/** The facts about a row that the local rules below can be asked about. */
export interface RowFacts {
  phase: string;
  needsReply: boolean;
  unread: boolean;
  jobId?: string;
}

/**
 * Whether a row we are already watching still belongs in this query.
 *
 * This exists for realtime removal only, and it is deliberately one-directional:
 * **local rules may remove a row, never add one.** Adding is the server's job,
 * because the read model is the authority on what matches. If a local rule
 * could also insert, a lagging projection and an eager client would disagree
 * and rows would flicker in and out.
 *
 * Conservative by construction: a clause the client cannot evaluate (hiring
 * stage, job location, anything custom) counts as "still matches", so an
 * unknown never causes a disappearance. Being wrong here should mean a row
 * lingers until the next re-query, never that one vanishes.
 */
export function stillMatches(row: RowFacts, query: InboxQuery): boolean {
  for (const filter of query.filters) {
    switch (filter.kind) {
      case 'status':
        if (filter.phases.length > 0 && !(filter.phases as string[]).includes(row.phase)) {
          return false;
        }
        break;
      case 'scope':
        // Both cuts are locally knowable, and each has to be checked against
        // its own signal: under `unread` a chat READ in another tab has to
        // leave the list, which `needsReply` would not notice (it only clears
        // on a reply). Same one-directional rule as everything here — a row
        // that stops matching goes, nothing is ever pulled back in.
        if (filter.value === 'unread' ? !row.unread : !row.needsReply) return false;
        break;
      case 'kind': {
        // Only the job/direct split is evaluated. The client CAN now see 'info'
        // (the channel carries its source, see `isBroadcast`), but this path may
        // only ever remove a row, and no panel builds a query whose kinds
        // disagree with the rows the server returned — so evaluating it could
        // only vanish a row that belongs, never rescue one that does not.
        const isJob = row.jobId != null;
        const wantsJob = filter.kinds.includes('job');
        const wantsDirect = filter.kinds.includes('direct');
        if (isJob && !wantsJob && !filter.kinds.includes('info')) return false;
        if (!isJob && !wantsDirect && !filter.kinds.includes('info')) return false;
        break;
      }
      case 'job':
        if (filter.jobIds.length > 0 && (!row.jobId || !filter.jobIds.includes(row.jobId))) {
          return false;
        }
        break;
      default:
        // search / application_status / jobLocation / custom: not locally
        // knowable. A search matches message bodies now, and the client cannot
        // tell which half returned a row, so judging it would vanish rows.
        break;
    }
  }
  return true;
}

/**
 * What a re-derived row should do about the cut it was selected under.
 *
 * `gone` — it does not belong in this list any more: an ended chat under a
 * `status: open` view. It leaves.
 * `settled` — it still belongs to the list, but it has left the *scope* cut the
 * Unread pill applies, because the employer just read or answered it.
 * `match` — nothing changed.
 */
export type RowFate = 'match' | 'settled' | 'gone';

/**
 * The same one-directional re-check as `stillMatches`, split so the Unread pill
 * can hold what it is showing.
 *
 * Opening a conversation deleted its row mid-click: the chat is on screen in
 * the pane, and the list beside it says there is nothing there. The cut is a
 * question the read model answered once, so a row that leaves it stays until
 * the question is asked again — a tab away and back, a filter change, a
 * reload. Only the number moves at once (InboxPanel subtracts the settled
 * rows), because a number is a claim about now.
 *
 * Every other clause still removes on the spot: those say where the row
 * belongs, not what the employer has already done with it. An ended chat under
 * an open-only view has to go — it belongs on the Ended rail.
 */
export function rowFate(row: RowFacts, query: InboxQuery): RowFate {
  if (!stillMatches(row, withoutFilter(query, 'scope'))) return 'gone';
  return stillMatches(row, query) ? 'match' : 'settled';
}
